import React from "react";
import LegalLayout, { LegalSection, LegalList } from "@/components/legal/LegalLayout";
import { ShieldCheck, Lock, Layers, KeyRound, Server, ScrollText, Activity, HardDrive, Workflow } from "lucide-react";

const TOC = [
  { id: "tls", label: "1. HTTPS / TLS Encryption" },
  { id: "auth", label: "2. Authentication Controls" },
  { id: "rbac", label: "3. Role-Based Permissions" },
  { id: "tenant-isolation", label: "4. Tenant Isolation" },
  { id: "audit", label: "5. Audit Logging" },
  { id: "cloud-storage", label: "6. Cloud Storage Security" },
  { id: "monitoring", label: "7. Infrastructure Monitoring" },
  { id: "backup", label: "8. Backup Practices" },
  { id: "operations", label: "9. Operational Controls" },
  { id: "questions", label: "10. Security Questions" },
];

const PILLARS = [
  { icon: Lock, label: "TLS in transit" },
  { icon: KeyRound, label: "JWT auth · rotated refresh" },
  { icon: Layers, label: "Tenant-scoped data" },
  { icon: ShieldCheck, label: "RBAC enforcement" },
  { icon: ScrollText, label: "Tamper-evident audit log" },
  { icon: Server, label: "Encrypted cloud storage" },
  { icon: Activity, label: "Health monitoring" },
  { icon: HardDrive, label: "Daily backups" },
  { icon: Workflow, label: "Least-privilege ops" },
];

export default function SecurityPage() {
  return (
    <LegalLayout
      testid="page-security"
      eyebrow="Trust · Security"
      title="ForgedOps Plans Security"
      intro="ForgedOps Plans is built for contractors, municipalities, state agencies, airports, and utility owners who run critical infrastructure work. This page describes the technical, operational, and organizational controls that protect Customer Data on the platform."
      toc={TOC}
      metaTitle="Security · ForgedOps Plans"
      metaDescription="Security controls on ForgedOps Plans — TLS encryption, JWT authentication, role-based permissions, tenant isolation, audit logging, encrypted cloud storage, monitoring, and backup practices."
      canonical="https://forgedopsplans.com/legal/security"
    >
      {/* Pillar grid — visual anchor for the rest of the page */}
      <section className="not-prose" data-testid="security-pillars">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-2">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.label}
                className="flex items-center gap-3 rounded-md border px-3.5 py-3"
                style={{ borderColor: "var(--fop-border)", background: "var(--fop-surface)" }}
              >
                <Icon size={18} style={{ color: "var(--fop-primary)" }} />
                <div className="text-[13px] font-mono uppercase tracking-[0.12em]" style={{ color: "var(--fop-text-2)" }}>
                  {p.label}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <LegalSection id="tls" title="1. HTTPS / TLS Encryption">
        <p>All traffic between client devices and the Platform is encrypted in transit using HTTPS over TLS 1.2+ with modern cipher suites. HTTP Strict Transport Security (HSTS) is enabled platform-wide so browsers refuse to fall back to unencrypted HTTP. Pre-signed cloud-storage URLs used to deliver plan PDFs are scoped to short time-to-live windows.</p>
      </LegalSection>

      <LegalSection id="auth" title="2. Authentication Controls">
        <p>User authentication uses short-lived JWT access tokens delivered as httpOnly, Secure cookies. Refresh tokens are stored as separate httpOnly cookies and rotated on use. Passwords are stored as salted bcrypt hashes; cleartext passwords are never logged. Brute-force protection and lockout thresholds apply to failed login attempts. Failed authentications against non-existent accounts are recorded so administrators can detect credential-stuffing activity.</p>
      </LegalSection>

      <LegalSection id="rbac" title="3. Role-Based Permissions">
        <p>Every action on the Platform is gated by role. Standard roles include Admin, Project Manager, Superintendent, Field Engineer, Foreman, Owner, and Viewer. Sensitive endpoints (user management, audit, platform administration) require elevated role checks. Module gating (e.g., photos, conflicts) is enforced server-side regardless of the client.</p>
      </LegalSection>

      <LegalSection id="tenant-isolation" title="4. Tenant Isolation">
        <p>Customer Data is partitioned by <code className="font-mono text-[13px]">tenant_id</code>. Every read, write, and storage operation is filtered against the authenticated user&rsquo;s tenant. Cross-tenant access is prohibited by API contract and verified by automated regression coverage. Cloud-storage object keys include the tenant identifier so a leaked object key cannot be replayed against a different tenant&rsquo;s storage prefix.</p>
      </LegalSection>

      <LegalSection id="audit" title="5. Audit Logging">
        <p>Every administrative, security-relevant, and data-changing action writes an immutable audit row capturing actor, action, target, IP address, user-agent, severity, success/failure, and a structured metadata payload. The audit log is queryable by tenant administrators and platform staff via the <code className="font-mono text-[13px]">/api/audit</code> endpoint. Retention can extend up to seven (7) years.</p>
      </LegalSection>

      <LegalSection id="cloud-storage" title="6. Cloud Storage Security">
        <p>Plan PDFs, photos, and exports are stored in an S3-compatible object store (Cloudflare R2) encrypted at rest. Objects are written to tenant-prefixed keys and served to authenticated users via short-lived pre-signed URLs only. Direct anonymous access is denied at the bucket policy layer. Credentials used by the Platform to write storage are stored as environment secrets — never in source control.</p>
      </LegalSection>

      <LegalSection id="monitoring" title="7. Infrastructure Monitoring">
        <p>The Platform exposes anonymous health endpoints (<code className="font-mono text-[13px]">/api/health</code>, <code className="font-mono text-[13px]">/api/system_health</code>) for liveness, readiness, and database, storage, and email configuration checks. Application errors and latency are continuously observed and alerted to the operations team.</p>
      </LegalSection>

      <LegalSection id="backup" title="8. Backup Practices">
        <p>The primary database is backed up on a daily schedule. Backups are retained for ninety (90) days on a rolling window and overwritten thereafter. Object-store contents are versioned where supported. Restore procedures are documented in an internal runbook and exercised on a recurring cadence.</p>
      </LegalSection>

      <LegalSection id="operations" title="9. Operational Controls">
        <LegalList items={[
          "Production access is limited to authorized engineering personnel under principle of least privilege.",
          "Secrets are managed as environment variables in the deployment platform — never checked into source control.",
          "All deployments are reviewed and traceable to a specific source revision.",
          "Third-party services used by the Platform (email delivery, object storage, identity) are vetted for security posture before integration.",
          "Security-relevant code paths (authentication, tenant isolation, audit) are covered by automated regression tests.",
        ]} />
      </LegalSection>

      <LegalSection id="questions" title="10. Security Questions">
        <p>To report a vulnerability, request a security questionnaire, or ask about our controls, contact:</p>
        <p>
          <a
            href="mailto:info@forgedopshq.com"
            className="text-base underline"
            style={{ color: "var(--fop-primary)" }}
            data-testid="security-contact-email"
          >
            info@forgedopshq.com
          </a>
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
